import { useState } from "react";
import { CalendarPlus, LayoutList, GanttChartSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Notify, toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth.store";
import { ProductionOrdersTable } from "./ProductionOrdersTable";
import { ProductionTimeline } from "./ProductionTimeline";
import { ProductionOrderFormDialog } from "./ProductionOrderFormDialog";
import { ProductionOrderIndicatorsDialog } from "./ProductionOrderIndicatorsDialog";
import { useCancelProductionOrder } from "./hooks";
import type { ProductionOrder } from "../../types/production-orders.types.js";

const TAB = "flex items-center gap-1.5 rounded px-3 py-1 text-sm transition-colors";

type FormState = { mode: "create" } | { mode: "edit"; order: ProductionOrder } | null;

// Orquestrador: toggle Timeline/Tabela + "Nova OP" (modal). Criar/editar e
// indicadores abrem em modal (pontos 2/3/5 do review); cancelamento confirma via Notify e mora no
// modal de edição (único surface de detalhe da OP após a remoção do painel lateral).
export function ProductionOrdersIndex() {
  const me = useAuthStore((s) => s.user);
  const isGestor = me?.role === "GESTOR";
  const cancel = useCancelProductionOrder();
  const [view, setView] = useState<"timeline" | "table">("timeline");
  const [form, setForm] = useState<FormState>(null);
  const [indicatorsOrder, setIndicatorsOrder] = useState<ProductionOrder | null>(null);

  const openEdit = (o: ProductionOrder) => setForm({ mode: "edit", order: o });
  const openIndicators = (o: ProductionOrder) => setIndicatorsOrder(o);
  // Linha/bloco/ação: GESTOR edita agendadas; demais estados → indicadores.
  const openOrder = (o: ProductionOrder) =>
    o.status === "SCHEDULED" && isGestor ? openEdit(o) : openIndicators(o);

  async function requestCancel(o: ProductionOrder): Promise<boolean> {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Cancelar a OP ${o.code ?? ""}?`.trim(),
      description: "A ordem não será iniciada automaticamente no horário planejado.",
      confirmLabel: "Cancelar OP",
      cancelLabel: "Voltar",
    });
    if (!ok) return false;
    try {
      await cancel.mutateAsync({ id: o.id });
      toast.success("Ordem cancelada.");
      return true;
    } catch (err) {
      toast.error("Falha ao cancelar a ordem.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
      return false;
    }
  }

  return (
    <div>
      <PageHeader
        title="Ordens de Produção"
        subtitle="Programe a produção com antecedência e acompanhe o histórico."
        actions={
          <>
            <div className="flex rounded-md border bg-card p-0.5">
              <button
                type="button"
                className={cn(TAB, view === "timeline" && "bg-muted font-medium")}
                onClick={() => setView("timeline")}
              >
                <GanttChartSquare className="h-4 w-4" /> Timeline
              </button>
              <button
                type="button"
                className={cn(TAB, view === "table" && "bg-muted font-medium")}
                onClick={() => setView("table")}
              >
                <LayoutList className="h-4 w-4" /> Tabela
              </button>
            </div>
            {isGestor ? (
              <Button onClick={() => setForm({ mode: "create" })}>
                <CalendarPlus className="mr-1 h-4 w-4" /> Nova OP
              </Button>
            ) : null}
          </>
        }
      />

      {view === "timeline" ? (
        <ProductionTimeline onOpenOrder={openOrder} />
      ) : (
        <ProductionOrdersTable onOpen={openOrder} />
      )}

      {form ? (
        <ProductionOrderFormDialog
          mode={form.mode}
          order={form.mode === "edit" ? form.order : undefined}
          canCancel={form.mode === "edit" && form.order.status === "SCHEDULED" && isGestor}
          onCancelOrder={
            form.mode === "edit"
              ? () => void requestCancel(form.order).then((done) => done && setForm(null))
              : undefined
          }
          onClose={() => setForm(null)}
        />
      ) : null}
      {indicatorsOrder ? (
        <ProductionOrderIndicatorsDialog
          order={indicatorsOrder}
          onClose={() => setIndicatorsOrder(null)}
        />
      ) : null}
    </div>
  );
}
